import MixpanelTracker from "./mixpanel-tracker";
import { config } from "./constants";

const GLOBAL_ERROR_TRACKING_INSTALLED_KEY = "__fwGlobalErrorTrackingInstalled";

function serializeErrorField(value: any) {
  if (value instanceof Error) {
    return { message: value.message || value.toString(), stack: value.stack || null };
  }

  if (typeof value === "string") {
    return { message: value, stack: null };
  }

  try {
    const serialized = JSON.stringify(value);
    return { message: serialized, stack: null };
  } catch (_) {
    return { message: String(value), stack: null };
  }
}

function toStatusCode(value: any): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && /^\d{3}$/.test(value)) return Number(value);
  return null;
}

export function installGlobalErrorTracking() {
  if (typeof window === "undefined") return;
  if ((window as any)[GLOBAL_ERROR_TRACKING_INSTALLED_KEY]) return;
  (window as any)[GLOBAL_ERROR_TRACKING_INSTALLED_KEY] = true;

  let reporting = false;

  // same guard as console.error patching would need - stops a tracking failure from causing a reporting loop
  const reportError = (details: Record<string, any>) => {
    if (reporting) return;

    reporting = true;
    try {
      MixpanelTracker.trackEvent("client_error", details);
    } catch (_) {
      // swallow - never let tracking itself throw
    } finally {
      reporting = false;
    }
  };

  const previousOnError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    const serializedMessage = serializeErrorField(message);
    const serializedError = serializeErrorField(error);

    reportError({
      source: "window",
      message: serializedMessage.message,
      stack: serializedError.stack || null,
      script: source,
      line: lineno,
      column: colno
    });

    if (typeof previousOnError === "function") return previousOnError(message, source, lineno, colno, error);
    return false;
  };

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    const serialized = serializeErrorField(event.reason);

    reportError({
      source: "unhandledrejection",
      message: serialized.message,
      stack: serialized.stack || null
    });
  });

  if (typeof PerformanceObserver !== "undefined") {
    const fundwavePatterns = [config.fundwaveServerExp, config.servicesExp];
    const trackedNetworkEntries = new Set<string>();

    const trackResourceTimingEntry = (entry: any) => {
      const initiatorType = entry?.initiatorType;
      if (!["fetch", "xmlhttprequest"].includes(initiatorType)) return;

      const url = entry?.name || "";
      if (!fundwavePatterns.some((pattern) => pattern.test(url))) return;

      const statusCode = toStatusCode(entry?.responseStatus);
      if (!Number.isFinite(statusCode) || (statusCode as number) < 400) return;

      const dedupeKey = `${url}|${entry.startTime}|${statusCode}|${initiatorType}`;
      if (trackedNetworkEntries.has(dedupeKey)) return;
      trackedNetworkEntries.add(dedupeKey);

      reportError({
        source: "network",
        message: `${initiatorType.toUpperCase()} ${url} failed with ${statusCode}`,
        statusCode,
        url
      });
    };

    try {
      (performance.getEntriesByType("resource") || []).forEach(trackResourceTimingEntry);

      const resourceObserver = new PerformanceObserver((list) => list.getEntries().forEach(trackResourceTimingEntry));
      resourceObserver.observe({ entryTypes: ["resource"] });
    } catch (_) {
      // responseStatus on resource timing entries isn't supported everywhere
    }
  }
}
